// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Box, Button, Group, Loader, Paper, ScrollArea, Stack, TextInput, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { formatHumanName } from '@medplum/core';
import { IconCalendarPlus, IconSearch } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AppointmentHistoryTable } from '../../../components/patient-appointments/AppointmentHistoryTable';
import { CareJourneyStepper } from '../../../components/patient-appointments/CareJourneyStepper';
import { CreateEncounterModal } from '../../../components/patient-appointments/CreateEncounterModal';
import { PhoneEncounterHistoryTable } from '../../../components/patient-appointments/PhoneEncounterHistoryTable';
import { PhoneEncounterModal } from '../../../components/patient-appointments/PhoneEncounterModal';
import { UpcomingAppointmentsTable } from '../../../components/patient-appointments/UpcomingAppointmentsTable';
import type { AppointmentDetails } from '../../../hooks/useAppointmentDetails';
import { useAppointmentDetails } from '../../../hooks/useAppointmentDetails';
import { usePatient } from '../../../hooks/usePatient';
import { usePatientAppointments } from '../../../hooks/usePatientAppointments';
import { usePatientPhoneEncounters } from '../../../hooks/usePatientPhoneEncounters';
import { usePatientTriageStage } from '../../../hooks/usePatientTriageStage';
import { useRole } from '../../../hooks/useRole';
import { formatAppointmentDate, formatAppointmentTime } from '../../../utils/appointment-formatting';
import { hasPermission } from '../../../utils/permissions';
import classes from './AppointmentsTab.module.css';

const scrollAreaStyles = {
  thumb: { backgroundColor: 'var(--mantine-color-polaris-5)' },
};

/**
 * Shared client-side search filter for both the upcoming and history tables.
 * Matches the query (case-insensitive) against the same text the table renders —
 * type, provider, formatted date/time, and payment status — so "June" or "3:30 PM"
 * works as expected.
 */
export function filterDetails(details: AppointmentDetails[], query: string): AppointmentDetails[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return details;
  }
  return details.filter((d) => {
    const hay = [
      d.appointmentType,
      d.providerName,
      formatAppointmentDate(d.appointment.start),
      formatAppointmentTime(d.appointment.start, d.appointment.end),
      d.paymentStatus,
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });
}

/**
 * Patient-scoped Appointments tab. Renders the Care Journey Stepper, an Upcoming
 * Appointments table, an Encounter History table, and a "Create Encounter"
 * entry point that opens the booking flow nested under the patient page.
 */
export function AppointmentsTab(): JSX.Element {
  const patient = usePatient();
  const navigate = useNavigate();
  const { role, isAdmin, loading: roleLoading } = useRole();
  const { triageStage, loading: triageLoading } = usePatientTriageStage(patient);
  const { upcoming, history, loading: apptsLoading, error: apptsError, refresh } = usePatientAppointments(patient);
  const {
    encounters: phoneEncounters,
    loading: phoneLoading,
    error: phoneError,
    refresh: refreshPhoneEncounters,
  } = usePatientPhoneEncounters(patient);
  const { details: upcomingDetails, loading: upcomingDetailsLoading } = useAppointmentDetails(upcoming);
  const { details: historyDetails, loading: historyDetailsLoading } = useAppointmentDetails(history);
  const [search, setSearch] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [createEncounterOpened, createEncounterHandlers] = useDisclosure(false);
  const [phoneEncounterOpened, phoneEncounterHandlers] = useDisclosure(false);

  const filteredUpcoming = useMemo(() => {
    return filterDetails(upcomingDetails, search);
  }, [upcomingDetails, search]);

  const filteredHistory = useMemo(() => {
    return filterDetails(historyDetails, historySearch);
  }, [historyDetails, historySearch]);

  if (!patient) {
    return (
      <Box p="md">
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      </Box>
    );
  }

  const canSchedule = hasPermission({ role, permission: 'schedule-appointment', isAdmin });
  const canConduct = hasPermission({ role, permission: 'conduct-appointment', isAdmin });

  const handleCreate = (): void => {
    navigate(`/Patient/${patient.id}/Appointment/schedule`);
  };

  const upcomingListLoading = apptsLoading || upcomingDetailsLoading;
  const historyListLoading = apptsLoading || historyDetailsLoading;

  return (
    <Stack gap="lg" p="md">
      {/* Care Journey Stepper block */}
      <Paper withBorder radius="md" shadow="sm" p={0} bg="white" className={classes.block}>
        {triageLoading ? (
          <Stack align="center" py="md">
            <Loader size="sm" />
          </Stack>
        ) : (
          <CareJourneyStepper triageStage={triageStage} />
        )}
      </Paper>

      {/* Upcoming Appointments block */}
      <Paper withBorder radius="md" shadow="sm" p="lg" className={classes.block}>
        <Group justify="space-between" align="center" mb="md" wrap="wrap">
          <Title order={3}>Upcoming Appointments</Title>
          <Group gap="sm">
            <TextInput
              placeholder="Search type, provider, date…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={14} />}
              size="sm"
              w={260}
            />
            <Button
              leftSection={<IconCalendarPlus size={16} />}
              onClick={createEncounterHandlers.open}
              disabled={roleLoading || !canSchedule}
              title={!canSchedule && !roleLoading ? 'Your role cannot schedule appointments' : undefined}
            >
              Create Encounter
            </Button>
          </Group>
        </Group>

        {apptsError || phoneError ? (
          <Alert color="red" variant="light">
            {(apptsError ?? phoneError)?.message}
          </Alert>
        ) : (
          <ScrollArea scrollbarSize={6} type="auto" styles={scrollAreaStyles}>
            <Box pb="md">
              <UpcomingAppointmentsTable
                appointments={filteredUpcoming}
                loading={upcomingListLoading}
                patientId={patient.id ?? ''}
                patientName={patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unnamed patient'}
                canSchedule={canSchedule}
                canConduct={canConduct}
                onRefresh={refresh}
              />
            </Box>
          </ScrollArea>
        )}
      </Paper>

      {/* Encounter History block */}
      <Paper withBorder radius="md" shadow="sm" p="lg" className={classes.block}>
        <Group justify="space-between" align="center" mb="md" wrap="wrap">
          <Title order={3}>Encounter History</Title>
          <TextInput
            placeholder="Search type, provider, date…"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.currentTarget.value)}
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={260}
          />
        </Group>

        {apptsError ? (
          <Alert color="red" variant="light">
            {apptsError.message}
          </Alert>
        ) : (
          <ScrollArea scrollbarSize={6} type="auto" styles={scrollAreaStyles}>
            <Box pb="md">
              <AppointmentHistoryTable
                appointments={filteredHistory}
                loading={historyListLoading}
                patientId={patient.id ?? ''}
              />
              <PhoneEncounterHistoryTable
                encounters={phoneEncounters}
                loading={phoneLoading}
                patientId={patient.id ?? ''}
                onRefresh={refreshPhoneEncounters}
              />
            </Box>
          </ScrollArea>
        )}
      </Paper>
      <CreateEncounterModal
        opened={createEncounterOpened}
        onClose={createEncounterHandlers.close}
        onCreateAppointment={() => {
          createEncounterHandlers.close();
          handleCreate();
        }}
        onCreatePhoneEncounter={() => {
          createEncounterHandlers.close();
          phoneEncounterHandlers.open();
        }}
      />
      <PhoneEncounterModal
        opened={phoneEncounterOpened}
        patient={patient}
        onClose={phoneEncounterHandlers.close}
        onSaved={refreshPhoneEncounters}
      />
    </Stack>
  );
}
